import React, { useState } from "react"

import { Link } from "gatsby"
import styled, { css } from "styled-components"

import { minD, maxD } from "../../../../assets/styles/helpers/devices"
import { UnstyledList } from "../../common/essentials"
import SiteLanguage from "../../../../helpers/siteLanguage"

const NavElementItem = styled.li`
position: relative;
--element-padding-vertical: 17px;
--element-padding-horizontal: 30px;

--element-font-size: 0.92rem;

@media ${maxD.laptopL} {
  --element-font-size: 0.85rem;
}

@media ${maxD.laptop} {
  --element-font-size: 0.75rem;
}

@media ${maxD.tabletL} {
  position: relative;
  --element-font-size: 1.25rem;
}

@media ${maxD.mobileS} {
  --element-font-size: 1.1rem;
}
`

const navLinkSharedStyles = css`
display: block;
position: relative;

font-size: var(--element-font-size);
font-weight: 500;

@media ${maxD.tabletL} {
  font-weight: 600;

  &::after {
    position: absolute;
    content: "";

    height: 2px;

    left: 0;
    bottom: 0;

    background-color: var(--mobile-nav-separator-gray);
  }
}
`

const NavLink = styled(Link)`
padding: var(--element-padding-vertical) calc(var(--element-padding-horizontal) * 0.8);
border-radius: 30px;

transition: color 200ms, background-color 200ms;

@media ${minD.tabletL} {
  text-align: center;

  &:hover {
    color: #fff;
    background-color: var(--main-green);
  }
}

@media ${maxD.desktop} {
  padding: calc(var(--element-padding-vertical) * 0.6)
    calc(var(--element-padding-horizontal) * 0.6);
}

@media ${maxD.laptopL} {
  padding: calc(var(--element-padding-vertical) * 0.3)
    calc(var(--element-padding-horizontal) * 0.3);
}

${navLinkSharedStyles}

@media ${maxD.tabletL} {
  padding: 0.8rem 0;

  &::after {
    width: 3.2rem;
  }
}
`

// Items whose WP link is just "#" only open their submenu: a <button>, styled
// like the links (it used to be a link to the current page).
const NavButton = styled(NavLink).attrs({ as: "button", type: "button" })`
  border: 0;
  background: none;
  font-family: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  color: inherit;
  cursor: pointer;

  @media ${maxD.tabletL} {
    width: 100%;
    text-align: left;
  }
`

const Chevron = styled.span`
display: none;

@media ${minD.tabletL} {
  display: inline-block;
  margin-left: 0.4rem;

  width: 0.4rem;
  height: 0.4rem;

  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;

  position: relative;
  top: -1px;

  transform: rotate(45deg);
  transition: transform 200ms ease;
  vertical-align: middle;

  ${NavElementItem}:hover &,
  ${NavElementItem}:focus-within & {
    transform: rotate(225deg);
  }
}
`

const SubNavContainer = styled.div`
@media ${maxD.tabletL} {
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
}
@media ${minD.tabletL} {
  position: absolute;
  top: calc(100% + 0.8rem);

  padding-top: 1.4rem;

  visibility: hidden;
  opacity: 0;
  transform: translateY(0.8rem);

  transition: transform 200ms, opacity 200ms, visibility 1ms 200ms;

  ${NavElementItem}:focus > &,
  ${NavElementItem}:focus-within > &, 
  ${NavElementItem}:hover > &,
  &:hover,
  &[data-open="true"] {
    visibility: visible;
    opacity: 1;
    transform: translateY(-1.2rem);

    transition: transform 200ms 101ms, opacity 200ms 101ms,
      visibility 1ms 100ms;
  }
}
`

const SubNav = styled(UnstyledList)`
@media ${minD.tabletL} {
  width: max-content;

  padding: calc(var(--element-padding-horizontal) * 0.7)
    calc(var(--element-padding-horizontal) * 1.5)
    calc(var(--element-padding-horizontal) * 0.7)
    calc(var(--element-padding-horizontal) * 0.7);
  border-radius: 10px;

  background-color: var(--white);

  &::before {
    position: absolute;
    content: "";

    top: 0.3rem;
    left: 1.5rem;

    border: 0.6rem solid transparent;
    border-bottom-color: var(--white);
  }
}

@media ${maxD.tabletL} {
  padding: 0.5rem 0 0 3rem;
}
`

const SubNavElement = styled.li`
@media ${minD.tabletL} {
  white-space: normal;

  ${SubNav} & {
    white-space: nowrap;
  }

  &:not(:last-of-type) {
    padding: 0 0 calc(var(--element-padding-horizontal) * 0.3);
  }
}

@media ${maxD.tabletL} {
  padding-bottom: 0.5rem;
}
`

const SubNavLink = styled(Link)`
@media ${minD.tabletL} {

  transition: color 200ms ease;

  &:hover {
    color: var(--main-green);
  }
}

${navLinkSharedStyles}

@media ${maxD.tabletL} {
  padding: 0.5rem 0;
  font-size: 1.1rem;

  &::after {
    width: 2rem;
  }
}
`

const SubNavTitle = styled.p`
font-weight: bold;
cursor: default;
font-size: var(--element-font-size);

@media ${maxD.tabletL} {
  font-size: 1.1rem;
  color: var(--brownish-gray);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
`

const SubNavTitleLink = styled(SubNavLink)`
font-weight: bold;
`

const MegaMenuContainer = styled.div`
@media ${maxD.tabletL} {
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  margin-top: 1rem;
}
@media ${minD.tabletL} {
  position: absolute;
  top: calc(100% + 0.8rem);
  left: 0;
  max-width: min(1200px, calc(100vw - 40px));

  display: flex;
  gap: 30px;

  padding-top: 1.4rem;

  visibility: hidden;
  opacity: 0;
  transform: translateY(0.8rem);

  transition: transform 200ms, opacity 200ms, visibility 1ms 200ms;

  ${NavElementItem}:focus > &,
  ${NavElementItem}:focus-within > &, 
  ${NavElementItem}:hover > &,
  &:hover,
  &[data-open="true"] {
    visibility: visible;
    opacity: 1;
    transform: translateY(-1.2rem);

    transition: transform 200ms 101ms, opacity 200ms 101ms, visibility 1ms 100ms;
  }
}
`
const MegaMenu = styled.div`
@media ${minD.tabletL} {
  display: flex;
  flex-wrap: nowrap;
  gap: 24px;
  border: 1px solid #E3E3E3;
  box-shadow: 0 1px 6px rgba(0,0,0,0.16);

  padding: calc(var(--element-padding-horizontal) * 0.7)
    calc(var(--element-padding-horizontal) * 1.5)
    calc(var(--element-padding-horizontal) * 0.7)
    calc(var(--element-padding-horizontal) * 0.7);

  background-color: var(--white);
  border-radius: 10px;
}
`

const MegaMenuColumn = styled.div`
@media ${minD.tabletL} {
  flex: 1 1 auto;
  min-width: 240px;
  max-width: 320px;
}
@media ${maxD.tabletL} {
  padding-left: 20px;

  ${SubNavElement}:not(:first-child) {
    padding-left: 1rem;
  }
}
`

const ToggleButton = styled.button.attrs({ type: "button" })`
padding: 0;
border: 0;
background: none;
cursor: pointer;

@media ${maxD.tabletL} {
  position: absolute;
  right: 0;
  top: 0;
  width: 45px;
  height: 45px;
  min-width: 45px;
  &::before {
    content: "";
    display: block;
    position: absolute;
    transition: .4s ease-in-out;
    width: 12px;
    height: 12px;
    top: 1rem;
    pointer-events: none;
    right: 1rem;
    border: 2px solid var(--dark-gray);
    transform: rotate(${({ $isOpen }) => ($isOpen ? "225deg" : "45deg")});
    border-top: none;
    border-left: none;
  }
}
@media ${minD.tabletL} {
  display: none;
}
`

// WP menu items without a page of their own have "#" as the link, which
// arrives here as "/#/" or "/pl/#/".
const isPlaceholderPath = path => !path || /(^|\/)#\/?$/.test(path)

const NavElement = ({ path, label, id, parentId, menu_item_is_title, childItems }) => {
  const subNavItems = childItems?.nodes || [];
  const hasSubNav = subNavItems.length !== 0;
  const [openNav, setOpenNav] = useState(false);
  const subNavId = `subnav-${id}`;

  const toggleNav = () => {
    setOpenNav(!openNav);
  }
  const closeOnEscape = event => {
    if (event.key === "Escape") setOpenNav(false);
  }
  const toggleLabel = SiteLanguage() === "en" ? `${label} – submenu` : `${label} – podmenu`;

  return (
    <NavElementItem onKeyDown={hasSubNav ? closeOnEscape : undefined}>
      {hasSubNav && isPlaceholderPath(path) ? (
        <NavButton aria-expanded={openNav} aria-controls={subNavId} onClick={toggleNav}>
          {label}
          <Chevron aria-hidden="true" />
        </NavButton>
      ) : (
        <NavLink to={path}>
          {label}
          {hasSubNav && <Chevron aria-hidden="true" />}
        </NavLink>
      )}
      {hasSubNav && !isPlaceholderPath(path) && (
        <ToggleButton
          $isOpen={openNav}
          onClick={toggleNav}
          aria-expanded={openNav}
          aria-controls={subNavId}
          aria-label={toggleLabel}
        ></ToggleButton>
      )}
      {hasSubNav && menu_item_is_title.megaMenu ? (
        <MegaMenuContainer id={subNavId} $isOpen={openNav} data-open={openNav}>
          <MegaMenu>
            {subNavItems.map(({ path, label, id, menu_item_is_title, childItems: { nodes: subNavItems } }) => (
              menu_item_is_title.menuItemIsTitle && (
              <MegaMenuColumn key={id}>
                <UnstyledList>
                  <SubNavElement>
                    {
                    path && !isPlaceholderPath(path) && path !== 'https://#' ?
                      <SubNavTitleLink to={path} className={ menu_item_is_title.menuItemIsTitle && 'NavList__SubTitle' }>{label}</SubNavTitleLink>
                    :
                      <SubNavTitle>{label}</SubNavTitle>
                    }
                  </SubNavElement>
                  {subNavItems && subNavItems.length !== 0 && (
                    subNavItems.map(({ path, label, id }) => (
                      <SubNavElement key={id}>
                        <SubNavLink to={path}>{label}</SubNavLink>
                      </SubNavElement>
                    ))
                  )}
                </UnstyledList>
              </MegaMenuColumn>
            )))}
          </MegaMenu>
        </MegaMenuContainer>
      ) : hasSubNav && !menu_item_is_title.megaMenu && (
        <SubNavContainer id={subNavId} $isOpen={openNav} data-open={openNav}>
          <SubNav>
            {subNavItems.map(({ path, label, id, menu_item_is_title }) => (
              <SubNavElement key={id}>
                <SubNavLink to={path} className={ menu_item_is_title.menuItemIsTitle && 'NavList__SubTitle' }>{label}</SubNavLink>
              </SubNavElement>
            ))}
          </SubNav>
        </SubNavContainer>
      )}
    </NavElementItem>
  )
}


export { NavElement }
